// Test Automation API Service
// Provides programmatic access to test results and automation

export interface TestResult {
  testName: string;
  status: 'pass' | 'fail' | 'skip';
  message: string;
  elementFound: boolean;
  elementSelector: string;
  duration?: number;
  error?: string;
}

export interface TestSummary {
  totalTests: number;
  passedTests: number;
  failedTests: number;
  skippedTests: number;
  successRate: number;
  duration: number;
  timestamp: string;
}

export interface TestSuite {
  id: string;
  name: string;
  results: TestResult[];
  summary: TestSummary;
  environment: {
    url: string;
    userAgent: string;
    viewport: string;
    timestamp: string;
  };
}

class TestAutomationAPI {
  private testSuites: TestSuite[] = [];
  private currentSuite: TestSuite | null = null;

  // Start a new test suite
  startTestSuite(name: string): string {
    const suiteId = `suite_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    this.currentSuite = {
      id: suiteId,
      name,
      results: [],
      summary: {
        totalTests: 0,
        passedTests: 0,
        failedTests: 0,
        skippedTests: 0,
        successRate: 0,
        duration: 0,
        timestamp: new Date().toISOString()
      },
      environment: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        timestamp: new Date().toISOString()
      }
    };

    this.testSuites.push(this.currentSuite);
    return suiteId;
  }

  // Add a test result to the current suite
  addTestResult(result: TestResult): void {
    if (!this.currentSuite) {
      throw new Error('No active test suite. Call startTestSuite() first.');
    }

    this.currentSuite.results.push(result);
    this.updateSummary();
  }

  // Update the summary statistics
  private updateSummary(): void {
    if (!this.currentSuite) return;

    const results = this.currentSuite.results;
    const totalTests = results.length;
    const passedTests = results.filter(r => r.status === 'pass').length;
    const failedTests = results.filter(r => r.status === 'fail').length;
    const skippedTests = results.filter(r => r.status === 'skip').length;
    const successRate = totalTests > 0 ? (passedTests / totalTests) * 100 : 0;

    this.currentSuite.summary = {
      totalTests,
      passedTests,
      failedTests,
      skippedTests,
      successRate,
      duration: this.calculateDuration(),
      timestamp: new Date().toISOString()
    };
  }

  // Calculate total duration
  private calculateDuration(): number {
    if (!this.currentSuite || this.currentSuite.results.length === 0) return 0;
    
    const durations = this.currentSuite.results
      .map(r => r.duration || 0)
      .filter(d => d > 0);
    
    return durations.reduce((sum, d) => sum + d, 0);
  }

  // Complete the current test suite
  completeTestSuite(): TestSuite | null {
    if (!this.currentSuite) return null;

    this.updateSummary();
    const completedSuite = { ...this.currentSuite };
    this.currentSuite = null;
    
    return completedSuite;
  }

  // Get all test suites
  getAllTestSuites(): TestSuite[] {
    return [...this.testSuites];
  }

  // Get a specific test suite by ID
  getTestSuite(suiteId: string): TestSuite | null {
    return this.testSuites.find(suite => suite.id === suiteId) || null;
  }

  // Get the latest test suite
  getLatestTestSuite(): TestSuite | null {
    return this.testSuites.length > 0 ? this.testSuites[this.testSuites.length - 1] : null;
  }

  // Clear all test suites
  clearAllTestSuites(): void {
    this.testSuites = [];
    this.currentSuite = null;
  }

  // Export test results as JSON
  exportTestResults(suiteId?: string): string {
    if (suiteId) {
      const suite = this.getTestSuite(suiteId);
      return suite ? JSON.stringify(suite, null, 2) : '{}';
    }
    return JSON.stringify(this.testSuites, null, 2);
  }

  // Get test statistics
  getTestStatistics(): {
    totalSuites: number;
    totalTests: number;
    averageSuccessRate: number;
    lastRun: string | null;
    mostRecentSuite: TestSuite | null;
  } {
    const totalSuites = this.testSuites.length;
    const totalTests = this.testSuites.reduce((sum, suite) => sum + suite.summary.totalTests, 0);
    const averageSuccessRate = totalSuites > 0 
      ? this.testSuites.reduce((sum, suite) => sum + suite.summary.successRate, 0) / totalSuites 
      : 0;
    const lastRun = totalSuites > 0 ? this.testSuites[this.testSuites.length - 1].summary.timestamp : null;
    const mostRecentSuite = this.getLatestTestSuite();

    return {
      totalSuites,
      totalTests,
      averageSuccessRate,
      lastRun,
      mostRecentSuite
    };
  }

  // Run automated tests and return results
  async runAutomatedTests(): Promise<TestSuite> {
    const suiteId = this.startTestSuite('Automated Test Run');
    
    try {
      // Import the browser test runner dynamically
      const { browserTestRunner } = await import('../utils/browserTestRunner');
      
      // Run all tests
      const testSuites = await browserTestRunner.runAllTests();
      
      // Process results
      for (const suite of testSuites) {
        for (const test of suite.tests) {
          this.addTestResult({
            testName: test.testName,
            status: test.status,
            message: test.message,
            elementFound: test.elementFound || false,
            elementSelector: test.elementSelector || '',
            duration: test.duration,
            error: test.error
          });
        }
      }
      
    } catch (error) {
      console.error('Error running automated tests:', error);
      this.addTestResult({
        testName: 'Test Execution',
        status: 'fail',
        message: `Failed to run tests: ${error}`,
        elementFound: false,
        elementSelector: '',
        error: error instanceof Error ? error.message : String(error)
      });
    }
    
    return this.completeTestSuite()!;
  }
}

// Create singleton instance
export const testAutomationAPI = new TestAutomationAPI();

// Expose globally for external access
(window as any).testAutomationAPI = testAutomationAPI;
